// Global tracking of open dropdowns
const openDropdowns: Set<() => void> = new Set();

// CMYK to RGB conversion function
const cmykToRgb = (c: number, m: number, y: number, k: number): string => {
    // Convert percentages to decimals
    c = c / 100;
    m = m / 100;
    y = y / 100;
    k = k / 100;

    // Convert CMYK to RGB
    const r = Math.round(255 * (1 - c) * (1 - k));
    const g = Math.round(255 * (1 - m) * (1 - k));
    const b = Math.round(255 * (1 - y) * (1 - k));

    return `rgb(${r}, ${g}, ${b})`;
};

// Parse CMYK string to RGB
const parseCmykToRgb = (cmykString: string): string | null => {
    const match = cmykString.match(/cmyk\((\d+)%,\s*(\d+)%,\s*(\d+)%,\s*(\d+)%\)/);
    if (match) {
        const [, c, m, y, k] = match.map(Number);
        return cmykToRgb(c, m, y, k);
    }
    return null;
};

export const createColorDropdown = (
    title: string,
    command: string,
    colorVariables: string[],
    contentRef: React.RefObject<HTMLDivElement>
): HTMLDivElement => {
    const dropdown = document.createElement('div');
    dropdown.className = 'rich-text-color-dropdown';
    dropdown.style.cssText = `
        position: relative;
        display: inline-block;
    `;

    const button = document.createElement('button');
    button.innerHTML = title;
    button.title = title;
    button.style.cssText = `
        padding: 6px 10px;
        border: 1px solid #ddd;
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
    `;

    const colorGrid = document.createElement('div');
    colorGrid.style.cssText = `
        position: absolute;
        top: 100%;
        left: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        padding: 8px;
        display: none;
        grid-template-columns: repeat(5, 1fr);
        gap: 4px;
        z-index: 10000;
        min-width: 200px;
    `;

    // Add "Remove Color" option first
    const removeColorBox = document.createElement('div');
    removeColorBox.style.cssText = `
        width: 24px;
        height: 24px;
        background: white;
        border: 2px solid #ff4444;
        border-radius: 2px;
        cursor: pointer;
        transition: transform 0.2s;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: #ff4444;
        font-weight: bold;
    `;

    removeColorBox.innerHTML = '×';
    removeColorBox.title = 'Remove Color';

    removeColorBox.addEventListener('mouseenter', () => {
        removeColorBox.style.transform = 'scale(1.1)';
        removeColorBox.style.backgroundColor = '#ffe6e6';
    });

    removeColorBox.addEventListener('mouseleave', () => {
        removeColorBox.style.transform = 'scale(1)';
        removeColorBox.style.backgroundColor = 'white';
    });

    removeColorBox.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (contentRef.current) {
            contentRef.current.focus();

            try {
                // Try execCommand first
                let success = false;
                if (command === 'foreColor') {
                    success = document.execCommand('removeFormat', false);
                } else if (command === 'backColor') {
                    success =
                        document.execCommand('hiliteColor', false, 'transparent') ||
                        document.execCommand('backColor', false, 'transparent');
                }

                // If execCommand fails, manually remove styles
                if (!success) {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        if (!range.collapsed) {
                            const selectedContent = range.extractContents();
                            const walker = document.createTreeWalker(
                                selectedContent,
                                NodeFilter.SHOW_ELEMENT,
                                null
                            );

                            const elements: Element[] = [];
                            let walkerNode = walker.nextNode();
                            while (walkerNode) {
                                elements.push(walkerNode as Element);
                                walkerNode = walker.nextNode();
                            }

                            // Remove color styles from all elements
                            elements.forEach((element) => {
                                if (element instanceof HTMLElement) {
                                    if (command === 'foreColor') {
                                        element.style.removeProperty('color');
                                        if (element.tagName.toLowerCase() === 'font') {
                                            element.removeAttribute('color');
                                        }
                                    } else if (command === 'backColor') {
                                        element.style.removeProperty('background-color');
                                        element.style.removeProperty('background');
                                    }

                                    if (element.style.length === 0) {
                                        element.removeAttribute('style');
                                    }
                                }
                            });

                            range.insertNode(selectedContent);

                            const newRange = document.createRange();
                            newRange.selectNodeContents(selectedContent);
                            selection.removeAllRanges();
                            selection.addRange(newRange);
                        }
                    }
                }
            } catch (error) {
                console.log('Color removal failed:', error);
            }

            closeDropdown();
        }
    });

    colorGrid.appendChild(removeColorBox);

    // Add color variables with CMYK to RGB conversion
    colorVariables.forEach((variable) => {
        const colorBox = document.createElement('div');

        // Directly read the CSS variable value from document root
        const rootStyles = getComputedStyle(document.documentElement);
        const rawValue = rootStyles.getPropertyValue(variable).trim();

        let backgroundColor = `var(${variable})`;
        let rgbColor: string | null = null;

        console.log(`Processing ${variable}: "${rawValue}"`);

        if (rawValue && rawValue.includes('cmyk')) {
            // Parse and convert CMYK to RGB
            rgbColor = parseCmykToRgb(rawValue);
            if (rgbColor) {
                backgroundColor = rgbColor;
                console.log(`Converted ${variable} from "${rawValue}" to "${rgbColor}"`);
            } else {
                console.log(`Failed to parse CMYK for ${variable}: "${rawValue}"`);
            }
        } else if (rawValue && !rawValue.includes('var(') && rawValue !== '') {
            // Use the raw value if it's a direct color (hex, rgb, etc.)
            backgroundColor = rawValue;
            rgbColor = rawValue;
            console.log(`Using direct color for ${variable}: "${rawValue}"`);
        } else {
            // Fallback: try to get computed style
            const tempElement = document.createElement('div');
            tempElement.style.backgroundColor = `var(${variable})`;
            tempElement.style.visibility = 'hidden';
            tempElement.style.position = 'absolute';
            document.body.appendChild(tempElement);

            try {
                const computedValue = getComputedStyle(tempElement).backgroundColor;
                if (
                    computedValue &&
                    computedValue !== 'transparent' &&
                    computedValue !== 'rgba(0, 0, 0, 0)'
                ) {
                    backgroundColor = computedValue;
                    rgbColor = computedValue;
                    console.log(`Computed color for ${variable}: "${computedValue}"`);
                }
            } catch (error) {
                console.log('Failed to compute color for', variable, error);
            } finally {
                document.body.removeChild(tempElement);
            }
        }

        colorBox.style.cssText = `
            width: 24px;
            height: 24px;
            background: ${backgroundColor};
            border: 1px solid #ddd;
            border-radius: 2px;
            cursor: pointer;
            transition: transform 0.2s;
            position: relative;
        `;

        colorBox.title = `${variable}${rgbColor ? ` (${rgbColor})` : ''}`;
        // Store the RGB color for use in execCommand
        if (rgbColor) {
            colorBox.setAttribute('data-rgb-color', rgbColor);
        }

        colorBox.addEventListener('mouseenter', () => {
            colorBox.style.transform = 'scale(1.1)';
        });

        colorBox.addEventListener('mouseleave', () => {
            colorBox.style.transform = 'scale(1)';
        });

        colorBox.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (contentRef.current) {
                // Focus back to the editor to maintain the selection
                contentRef.current.focus();

                try {
                    // Use the stored RGB color or try to get computed color
                    const colorToUse =
                        colorBox.getAttribute('data-rgb-color') ||
                        getComputedStyle(colorBox).backgroundColor;

                    const success = document.execCommand(command, false, colorToUse);

                    if (!success) {
                        // Fallback: apply the color directly as a style
                        const selection = window.getSelection();
                        if (selection && selection.rangeCount > 0) {
                            const range = selection.getRangeAt(0);
                            if (!range.collapsed) {
                                const span = document.createElement('span');
                                const colorProperty =
                                    command === 'foreColor' ? 'color' : 'background-color';
                                span.style.setProperty(colorProperty, colorToUse);
                                range.surroundContents(span);
                            }
                        }
                    }
                } catch (error) {
                    console.log('Color application failed:', error);
                }

                closeDropdown();
            }
        });

        colorGrid.appendChild(colorBox);
    });

    // Global click listener for closing dropdown
    let documentClickListener: ((e: Event) => void) | null = null;

    const closeDropdown = () => {
        colorGrid.style.display = 'none';
        if (documentClickListener) {
            document.removeEventListener('mousedown', documentClickListener);
            documentClickListener = null;
        }
        openDropdowns.delete(closeDropdown);
    };

    const openDropdown = () => {
        // Close all other open dropdowns first
        openDropdowns.forEach((closeFunc) => closeFunc());
        openDropdowns.clear();

        colorGrid.style.display = 'grid';
        openDropdowns.add(closeDropdown);

        // Set up document click listener to close dropdown
        setTimeout(() => {
            documentClickListener = (e: Event) => {
                const target = e.target as Element;
                if (!dropdown.contains(target)) {
                    closeDropdown();
                }
            };
            document.addEventListener('mousedown', documentClickListener);
        }, 100);
    };

    button.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOpen = colorGrid.style.display === 'grid';
        if (isOpen) {
            closeDropdown();
        } else {
            openDropdown();
        }
    });

    // Store cleanup function for later use
    (dropdown as any)._cleanup = () => {
        closeDropdown(); // This will also remove from openDropdowns set
    };

    dropdown.appendChild(button);
    dropdown.appendChild(colorGrid);

    return dropdown;
};
